/* eslint-disable complexity */
'use client';

import {
	For,
	Grid,
	Stack,
	Text,
} from '@chakra-ui/react';
import { LoadingSpinner, Pagination } from '@packages/chakra-v3';
import {
	ProductDetail,
	ProductImageGallery,
	ProductReviewCard,
	ProductReviewSummaryCard,
} from '../../components';

import { useGetProductBySlugQuery, useListReviewsQuery } from '../../hooks';
import { useQueryParams } from '@/modules/shared';
import { WebContractReviewQueryParamsSchema } from '@packages/contracts';
import { useCartActions } from '@/modules/cart';
import { DefaultStyle } from '@packages/wastic-shared';

type ProductDetailContainerProps = {
	productSlug: string;
};

const ProductDetailContainer = ({ productSlug }: ProductDetailContainerProps) => {
	const { queryParams, setQueryParams } = useQueryParams({
		schema:        WebContractReviewQueryParamsSchema,
		defaultValues: { slug: productSlug },
	});

	const { data: product, isLoading } = useGetProductBySlugQuery(productSlug);
	const { data: review } = useListReviewsQuery(queryParams);
	const { handleAddCartItem } = useCartActions();

	if (isLoading) return <LoadingSpinner />;

	if (!product) return <Text textStyle={'h4'}>Product not found</Text>;

	const reviewData = review?.reviews.map(reviewItem => ({
		id:         reviewItem.id,
		comment:    reviewItem.comment,
		images:     reviewItem.images,
		rating:     reviewItem.rating,
		name:       'Anonymous',
		reviewDate: new Date(reviewItem.createdAt).toLocaleDateString(),
	}));

	const pagination = review?.meta.pagination;

	return (
		<Stack gap={DefaultStyle.CONTENT_GAP}>
			<Grid
				gap={DefaultStyle.SECTION_GAP}
				templateColumns={{
					base: '1fr',
					lg:   '1fr 1fr',
					xl:   '3fr 2fr',
				}}
			>
				<ProductImageGallery
					coverImage={product?.coverImage}
					images={product?.images ?? []}
				/>
				<ProductDetail
					product={product}
					onClickAddToCart={handleAddCartItem}
				/>
			</Grid>
			<Stack gap={DefaultStyle.SMALL_SECTION_GAP}>
				<ProductReviewSummaryCard productReviewSummary={product.productReviewSummary} />
				{reviewData && reviewData.length > 0  && (
					<Stack gap={DefaultStyle.TIGHT_SECTION_GAP}>
						<Text textStyle={'h4'}> Recent Reviews</Text>
						<For each={reviewData}>
							{review => (
								<ProductReviewCard
									key={review.id}
									content={review.comment}
									images={review.images}
									name={'Anonymous'}
									rating={review.rating}
									reviewDate={review.reviewDate}
								/>
							)}
						</For>
						{pagination && (
							<Pagination
								count={pagination?.total}
								page={pagination.page}
								pageSize={pagination.pageSize}
								onPageChange={({ page }) => setQueryParams('page', page)}
							/>
						)}
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};

export default ProductDetailContainer;